import { KeyRound, ArrowRight, AlertTriangle, CheckCircle, Clock, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Nip41ChainState } from '@/lib/nip41';
import { NIP41_CONTESTATION_WINDOW_DAYS } from '@/lib/nip41';

interface KeyChainDiagramProps {
  chain: Nip41ChainState;
  compact?: boolean;
}

export function KeyChainDiagram({ chain, compact }: KeyChainDiagramProps) {
  return (
    <div className={cn('flex flex-col items-center', compact ? 'space-y-2' : 'space-y-4')}>
      {/* Predecessor */}
      {chain.predecessor && (
        <>
          <KeyNode state={chain.predecessor} label="Predecessor" compact={compact} />
          <ArrowRight className="w-4 h-4 text-slate-600 rotate-90" />
        </>
      )}
      
      {/* Current */}
      <KeyNode state={chain} label="Current" isCurrent compact={compact} />
      
      {/* Successor */}
      {chain.successor && (
        <>
          <ArrowRight className="w-4 h-4 text-slate-600 rotate-90" />
          <KeyNode state={chain.successor} label="Successor" compact={compact} />
        </>
      )}
    </div>
  );
}

function KeyNode({
  state,
  label,
  isCurrent,
  compact,
}: {
  state: Nip41ChainState;
  label: string;
  isCurrent?: boolean;
  compact?: boolean;
}) {
  const statusConfig = {
    unarmed: { icon: KeyRound, color: 'text-slate-400', bg: 'bg-slate-900', border: 'border-slate-700' },
    armed: { icon: Shield, color: 'text-cyan-400', bg: 'bg-cyan-950/30', border: 'border-cyan-500/40' },
    migrating: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-950/30', border: 'border-amber-500/40' },
    settled: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-950/30', border: 'border-green-500/40' },
    contested: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-950/30', border: 'border-red-500/40' },
  };

  const config = statusConfig[state.state];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'flex items-center space-x-3 rounded-xl border p-3 transition-all',
        config.bg,
        config.border,
        isCurrent && !compact && 'ring-1 ring-cyan-500/30 shadow-lg shadow-cyan-500/10',
        compact && 'scale-90'
      )}
    >
      <div className={cn('p-2 rounded-lg bg-black/40', config.color)}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-slate-500 uppercase tracking-wider">{label}</span>
          <StatusBadge state={state.state} />
        </div>
        <p className={cn('font-mono truncate', compact ? 'text-xs' : 'text-sm', config.color)}>
          {state.npub.slice(0, 20)}...{state.npub.slice(-8)}
        </p>
      </div>
    </div>
  );
}

function StatusBadge({ state }: { state: Nip41ChainState['state'] }) {
  const config = {
    unarmed: { text: 'Unarmed', class: 'bg-slate-800 text-slate-400 border-slate-700' },
    armed: { text: 'Armed', class: 'bg-cyan-950/50 text-cyan-400 border-cyan-500/30' },
    migrating: { text: 'In Window', class: 'bg-amber-950/50 text-amber-400 border-amber-500/30' },
    settled: { text: 'Settled', class: 'bg-green-950/50 text-green-400 border-green-500/30' },
    contested: { text: 'Contested', class: 'bg-red-950/50 text-red-400 border-red-500/30' },
  };

  const c = config[state];
  return (
    <span className={cn('text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wider', c.class)}>
      {c.text}
    </span>
  );
}

interface WindowCountdownProps {
  firstSeenAt: number;
}

export function WindowCountdown({ firstSeenAt }: WindowCountdownProps) {
  const endAt = firstSeenAt + NIP41_CONTESTATION_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const remaining = Math.max(0, endAt - now);
  const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const progress = Math.min(100, ((now - firstSeenAt) / (endAt - firstSeenAt)) * 100);

  if (remaining === 0) {
    return (
      <div className="flex items-center space-x-2 text-green-400">
        <CheckCircle className="w-4 h-4" />
        <span className="text-sm font-medium">Window closed — migration settled</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center space-x-2 text-amber-400">
          <Clock className="w-4 h-4 animate-pulse" />
          <span className="font-medium">Contestation window open</span>
        </div>
        <span className="font-mono text-amber-400">{days}d {hours}h remaining</span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-500 rounded-full transition-all duration-1000"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-slate-500">
        Followers should not auto-update until this window closes. First seen: {new Date(firstSeenAt).toLocaleDateString()}
      </p>
    </div>
  );
}

interface SignerBadgeProps {
  pubkey: string;
  label: string;
  color?: 'cyan' | 'amber' | 'green';
}

export function SignerBadge({ pubkey, label, color = 'cyan' }: SignerBadgeProps) {
  const colorMap = {
    cyan: 'bg-cyan-950/30 border-cyan-500/30 text-cyan-400',
    amber: 'bg-amber-950/30 border-amber-500/30 text-amber-400',
    green: 'bg-green-950/30 border-green-500/30 text-green-400',
  };

  return (
    <div className={cn('flex items-center space-x-2 px-3 py-2 rounded-lg border', colorMap[color])}>
      <KeyRound className="w-4 h-4" />
      <div className="flex flex-col">
        <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
        <span className="font-mono text-xs">{pubkey.slice(0, 16)}...{pubkey.slice(-8)}</span>
      </div>
    </div>
  );
}
